import React, { useCallback, useEffect, useState } from 'react';
import { StepProps } from '../MultiStepModal';
import { clientsApi } from '../../../utils/api';

interface Client { id: string; name: string; email?: string; phone: string; address: string; postcode?: string; }
interface Site { id: string; name: string; address: string; postcode?: string; }

export default function JobClientSiteStep({ data, onDataChange }: StepProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [mode, setMode] = useState<'existing' | 'new'>(data.isNewCustomer ? 'new' : 'existing');
  const [siteMode, setSiteMode] = useState<'existing' | 'new'>(data.isNewSite ? 'new' : 'existing');

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await clientsApi.getAll();
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : payload?.data;
      setClients(Array.isArray(list) ? list : []);
    } catch { setClients([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const fetchSites = useCallback(async (_clientId: string) => {
    // Sites endpoint not yet available — user creates new site inline
    setSites([]);
  }, []);

  const selectClient = (c: Client) => {
    onDataChange({
      customerId: c.id, customerName: c.name, customerEmail: c.email || '',
      customerPhone: c.phone, customerAddress: c.address, postcode: c.postcode || '',
      isNewCustomer: false, siteId: '', isNewSite: false,
    });
    setSites([]); setSiteMode('existing');
    fetchSites(c.id);
  };

  const filtered = clients.filter(c =>
    !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.phone?.includes(clientSearch) || c.address?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Client & site</h3>
        <p className="step-form-subtitle">Select an existing client or add a new one, then choose the job site.</p>
      </div>

      {/* Client section */}
      <div className="mb-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[12px] font-semibold text-[#374151]">Client</span>
          <div className="flex rounded-lg border border-[#E6E6E4] overflow-hidden">
            {(['existing', 'new'] as const).map(m => (
              <button key={m} type="button"
                onClick={() => { setMode(m); onDataChange({ isNewCustomer: m === 'new', customerId: '', customerName: '', customerPhone: '', customerAddress: '', postcode: '' }); }}
                className={`px-3 py-1 text-[11px] font-medium transition-colors ${mode === m ? 'bg-[#1B1B1B] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                {m === 'existing' ? 'Existing' : '+ New'}
              </button>
            ))}
          </div>
        </div>

        {mode === 'existing' ? (
          <div>
            <input
              type="text" placeholder="Search clients…"
              value={clientSearch} onChange={e => setClientSearch(e.target.value)}
              className="mb-2 w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]"
            />
            {loading ? (
              <p className="text-[12px] text-gray-400 py-3">Loading clients…</p>
            ) : (
              <div className="max-h-48 overflow-auto rounded-lg border border-[#E6E6E4] divide-y divide-[#F3F4F6]">
                {filtered.length === 0 ? (
                  <p className="px-4 py-3 text-[12px] text-gray-400">No clients found</p>
                ) : filtered.map(c => (
                  <button key={c.id} type="button" onClick={() => selectClient(c)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] transition-colors ${data.customerId === c.id ? 'bg-[#F8F8F8]' : ''}`}>
                    <div className="h-7 w-7 rounded-full bg-[#E5E7EB] flex items-center justify-center text-[11px] font-semibold text-[#374151] flex-shrink-0">
                      {c.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-[#191919] truncate">{c.name}</p>
                      <p className="text-[11px] text-[#6B7280] truncate">{c.phone} · {c.address}</p>
                    </div>
                    {data.customerId === c.id && (
                      <span className="ml-auto flex-shrink-0 h-4 w-4 rounded-full bg-[#1B1B1B] flex items-center justify-center">
                        <svg className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 12 12" strokeWidth="2"><path d="M2 6l3 3 5-5"/></svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {[
              { field: 'customerName', label: 'Full name *', placeholder: 'John Smith' },
              { field: 'customerPhone', label: 'Phone *', placeholder: '07700 900000' },
              { field: 'customerEmail', label: 'Email', placeholder: 'john@example.com' },
              { field: 'postcode', label: 'Postcode', placeholder: 'SW1A 1AA' },
            ].map(({ field, label, placeholder }) => (
              <div key={field}>
                <label className="block text-[11px] font-medium text-[#374151] mb-1">{label}</label>
                <input type="text" placeholder={placeholder} value={data[field] || ''}
                  onChange={e => onDataChange({ [field]: e.target.value })}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-medium text-[#374151] mb-1">Address *</label>
              <input type="text" placeholder="123 High Street, London" value={data.customerAddress || ''}
                onChange={e => onDataChange({ customerAddress: e.target.value })}
                className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
            </div>
          </div>
        )}
      </div>

      {/* Site section — only shown once a client is chosen */}
      {(data.customerId || mode === 'new') && (
        <div className="border-t border-[#F3F4F6] pt-4">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[12px] font-semibold text-[#374151]">Job site</span>
            {sites.length > 0 && (
              <div className="flex rounded-lg border border-[#E6E6E4] overflow-hidden">
                {(['existing', 'new'] as const).map(m => (
                  <button key={m} type="button"
                    onClick={() => { setSiteMode(m); onDataChange({ isNewSite: m === 'new', siteId: '', siteName: '', siteAddress: '' }); }}
                    className={`px-3 py-1 text-[11px] font-medium transition-colors ${siteMode === m ? 'bg-[#1B1B1B] text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                    {m === 'existing' ? 'Existing site' : '+ New site'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {siteMode === 'existing' && sites.length > 0 ? (
            <div className="max-h-36 overflow-auto rounded-lg border border-[#E6E6E4] divide-y divide-[#F3F4F6]">
              {sites.map((s: Site) => (
                <button key={s.id} type="button" onClick={() => onDataChange({ siteId: s.id, siteName: s.name, siteAddress: s.address, isNewSite: false })}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FAFAFA] ${data.siteId === s.id ? 'bg-[#F8F8F8]' : ''}`}>
                  <p className="text-[12px] font-medium text-[#191919]">{s.name}</p>
                  <p className="ml-2 text-[11px] text-[#6B7280]">{s.address}</p>
                  {data.siteId === s.id && <span className="ml-auto text-[#1B1B1B]">✓</span>}
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <div>
                <label className="block text-[11px] font-medium text-[#374151] mb-1">Site name</label>
                <input type="text" placeholder="e.g. Main Office" value={data.siteName || ''}
                  onChange={e => onDataChange({ siteName: e.target.value, isNewSite: true })}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-[#374151] mb-1">Site postcode</label>
                <input type="text" placeholder="SW1A 1AA" value={data.sitePostcode || ''}
                  onChange={e => onDataChange({ sitePostcode: e.target.value, isNewSite: true })}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[11px] font-medium text-[#374151] mb-1">Site address</label>
                <input type="text" placeholder="Same as client or different" value={data.siteAddress || ''}
                  onChange={e => onDataChange({ siteAddress: e.target.value, isNewSite: true })}
                  className="w-full rounded-lg border border-[#E6E6E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1B1B1B]" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
